import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Wallet as WalletIcon, Loader2, Check, X, ExternalLink } from "lucide-react";

/**
 * XamanConnect — opens a Xaman SignIn payload (real QR via xumm.app) and
 * polls until the user signs. On success, calls `/caul-xrpl/xaman/link-wallet`
 * to persist the verified XRPL address.
 *
 * Drop this anywhere on a logged-in page. Receives `currentAddress` (optional)
 * so users with an already-attached address see a "Reconnect" affordance.
 */
export default function XamanConnect({ currentAddress = null, onLinked = () => {} }) {
  const [payload, setPayload] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);

  const start = async () => {
    setLoading(true);
    setStatus(null);
    try {
      const { data } = await api.post("/caul-xrpl/xaman/signin-payload");
      if (!data?.uuid) {
        toast.error("Xaman didn't return a payload");
        return;
      }
      setPayload(data);
      setPolling(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start Xaman sign-in");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!polling || !payload?.uuid) return;
    const t0 = Date.now();
    const interval = setInterval(async () => {
      // Auto-stop after 10 minutes
      if (Date.now() - t0 > 10 * 60 * 1000) {
        clearInterval(interval);
        setPolling(false);
        setStatus({ expired: true });
        return;
      }
      try {
        const { data } = await api.get(`/caul-xrpl/xaman/payload/${payload.uuid}`);
        if (data.resolved) {
          clearInterval(interval);
          setPolling(false);
          if (data.signed && data.account) {
            // Link to user
            try {
              await api.post("/caul-xrpl/xaman/link-wallet", { uuid: payload.uuid });
              toast.success("Xaman wallet linked");
              setStatus({ linked: true, account: data.account });
              onLinked(data.account);
            } catch (e) {
              toast.error(e.response?.data?.detail || "Linking failed");
              setStatus({ error: e.response?.data?.detail || "linking-failed" });
            }
          } else {
            setStatus({ rejected: true });
          }
        }
      } catch (e) {
        // transient — keep polling
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [polling, payload, onLinked]);

  return (
    <div className="parchment p-5 space-y-4" data-testid="xaman-connect">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="label-mystic">XRPL · Xaman wallet</div>
          <h3 className="font-serif text-2xl mt-1">Connect your wallet</h3>
          <p className="text-foreground/65 text-sm mt-2 max-w-md">
            Connect Xaman to receive real on-chain CAUL once you open a trustline.
            Your XRPL address is verified through Xaman — no manual entry, no
            mistakes.
          </p>
        </div>
        {currentAddress && (
          <span className="text-[10px] font-mono text-secondary border border-secondary/30 px-2 py-1 rounded-sm" data-testid="xaman-current-address">
            {currentAddress.slice(0, 6)}…{currentAddress.slice(-6)}
          </span>
        )}
      </div>

      {!payload && (
        <button onClick={start} disabled={loading}
          className="bg-primary text-primary-foreground px-5 py-2.5 uppercase tracking-[0.2em] text-xs rounded-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
          data-testid="xaman-start-btn">
          {loading ? <><Loader2 size={12} className="animate-spin"/> Preparing QR…</> : <><WalletIcon size={12}/> {currentAddress ? "Reconnect Xaman" : "Connect Xaman"}</>}
        </button>
      )}

      {payload && polling && !status && (
        <div className="space-y-3" data-testid="xaman-qr">
          <img src={payload.qrPng} alt="Scan with Xaman" className="w-56 h-56 mx-auto bg-white p-2 rounded-sm" data-testid="xaman-qr-img" />
          <div className="text-center text-xs text-foreground/65">
            Scan with Xaman, or
            {" "}
            <a href={payload.nextAlways} target="_blank" rel="noreferrer" className="text-secondary underline inline-flex items-center gap-1" data-testid="xaman-deep-link">
              open in Xaman <ExternalLink size={10}/>
            </a>
          </div>
          <div className="text-center text-[10px] text-foreground/45 uppercase tracking-wider">
            Waiting for signature…
          </div>
        </div>
      )}

      {status?.linked && (
        <div className="text-sm text-secondary inline-flex items-center gap-2" data-testid="xaman-linked">
          <Check size={14}/> Linked · <span className="font-mono">{status.account.slice(0, 8)}…{status.account.slice(-6)}</span>
        </div>
      )}
      {status?.rejected && (
        <div className="text-sm text-foreground/65 inline-flex items-center gap-2" data-testid="xaman-rejected">
          <X size={14}/> Sign-in was rejected.
        </div>
      )}
      {status?.expired && (
        <div className="text-sm text-foreground/65 inline-flex items-center gap-2" data-testid="xaman-expired">
          <X size={14}/> Payload expired.
        </div>
      )}
    </div>
  );
}
